import {
  OpportunityRow,
  PaginatedOpportunities,
} from "pages/sales-tools/opportunities/models";
import { makeApiRequestToUrl } from "utils/api-client";
import { SalesSearchOrganisation } from "./models";

async function fetchSalesOpportunities(
  limit: number,
  cursor = "",
): Promise<PaginatedOpportunities> {
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/sales/opportunities?${params.toString()}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.detail || "Failed to fetch opportunities");
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch admin opportunities: ${err}`);
  }
}

async function fetchSalesOpportunity(oppId: string): Promise<OpportunityRow> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/sales/opportunities/${oppId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.detail || "Failed to fetch opportunity");
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch admin opportunity '${oppId}': ${err}`);
  }
}

async function fetchOrganisationsSalesSearch(
  query: string,
): Promise<SalesSearchOrganisation[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/sales/organisation/search?query=${encodeURIComponent(query)}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch organisations with query '${query}' : ${err}`,
    );
  }
}

export interface ProductCatalogueLayer {
  config_name: string;
  layer_name: string;
  table_ref: string | null;
}

export interface ProductCatalogueEntry {
  product_key: string;
  product_name: string;
  product_description: string;
  product_category: string;
  config_names: string[];
  layers: ProductCatalogueLayer[];
  price_cents: number;
}

export interface ProductCatalogueResponse {
  pricebook_label: string;
  products: ProductCatalogueEntry[];
}

async function fetchProductCatalogue(): Promise<ProductCatalogueResponse> {
  const response = await makeApiRequestToUrl(
    `${import.meta.env.VITE_DELIVERY_API_URL}`,
    `/sales/products/catalogue`,
    "GET",
  );
  const responseData = await response.json();
  if (response.status !== 200) {
    throw new Error(responseData.detail || "Failed to fetch product catalogue");
  }
  return responseData;
}

export {
  fetchSalesOpportunities,
  fetchSalesOpportunity,
  fetchOrganisationsSalesSearch,
  fetchProductCatalogue,
};
